import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Plus, Sparkles, TrendingUp, Clock, Hammer } from "lucide-react";
import * as Icons from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { ProjectCard, ProjectCardSkeleton } from "@/components/ProjectCard";
import { Button } from "@/components/ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1781341031184-a3b2cd7f20ec?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function Home() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState(null);
  const [popular, setPopular] = useState(null);
  const [recent, setRecent] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/projects/featured").then((r) => setFeatured(r.data)).catch(() => setFeatured([]));
    api.get("/projects/popular").then((r) => setPopular(r.data)).catch(() => setPopular([]));
    api.get("/projects/recent").then((r) => setRecent(r.data)).catch(() => setRecent([]));
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6 fade-up">
              <p className="eyebrow text-primary flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> Comunidade maker brasileira
              </p>
              <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
                Transforme <span className="text-primary">madeira</span> em ideias.
              </h1>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground">
                Encontre projetos de móveis feitos com paletes, aprenda novas técnicas e
                compartilhe suas próprias criações com milhares de marceneiros.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button data-testid="hero-explore-btn" size="lg" onClick={() => navigate("/explorar")} className="rounded-full gap-2 font-semibold">
                  Explorar projetos <ArrowRight className="h-4 w-4" />
                </Button>
                <Button data-testid="hero-publish-btn" size="lg" variant="outline" onClick={() => navigate("/publicar")} className="rounded-full gap-2 font-semibold">
                  <Plus className="h-4 w-4" /> Publicar meu projeto
                </Button>
              </div>
            </div>
            <div className="lg:col-span-6 fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative rounded-3xl overflow-hidden border border-border shadow-2xl">
                <img src={HERO_IMG} alt="Sofá de paletes" className="aspect-[5/4] w-full object-cover" />
                <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-background/85 glass p-4 flex items-center justify-between">
                  <div>
                    <p className="eyebrow text-primary">Projeto em destaque</p>
                    <p className="font-bold">Sofá de palete para varanda</p>
                  </div>
                  <span className="blueprint-badge rounded-lg px-2.5 py-1 text-xs">200×70×80 cm</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Featured */}
        <Section title="Projetos em destaque" icon={Sparkles} to="/explorar">
          <Grid>
            {featured === null
              ? Array.from({ length: 3 }).map((_, i) => <ProjectCardSkeleton key={i} />)
              : featured.slice(0, 6).map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
          </Grid>
        </Section>

        {/* Categories */}
        <section className="py-10">
          <div className="flex items-end justify-between mb-6">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
              <Hammer className="h-6 w-6 text-primary" /> Categorias
            </h2>
            <Link to="/categorias" className="text-sm font-medium text-primary hover:underline">Ver todas</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {categories.map((c) => {
              const Icon = Icons[c.icon] || Icons.Boxes;
              return (
                <Link
                  key={c.slug}
                  to={`/explorar?category=${c.slug}`}
                  data-testid={`category-${c.slug}`}
                  className="group flex flex-col items-center gap-2 rounded-2xl border border-border bg-card p-5 text-center transition-all hover:border-primary hover:bg-secondary"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-secondary text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="text-sm font-semibold leading-tight">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{c.count} projeto(s)</span>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Popular */}
        <Section title="Projetos populares" icon={TrendingUp} to="/explorar?sort=popular">
          <Grid>
            {popular === null
              ? Array.from({ length: 4 }).map((_, i) => <ProjectCardSkeleton key={i} />)
              : popular.slice(0, 4).map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
          </Grid>
        </Section>

        {/* Recent */}
        <Section title="Projetos recentes" icon={Clock} to="/explorar?sort=recent">
          <Grid>
            {recent === null
              ? Array.from({ length: 4 }).map((_, i) => <ProjectCardSkeleton key={i} />)
              : recent.slice(0, 4).map((p, i) => <ProjectCard key={p.id} project={p} index={i} />)}
          </Grid>
        </Section>
      </div>

      {/* CTA */}
      <section className="mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-primary px-6 py-14 sm:px-14 text-primary-foreground wood-grain">
            <div className="relative max-w-2xl">
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Fez alguma coisa com paletes?</h2>
              <p className="mt-3 text-primary-foreground/85 text-base">
                Compartilhe seu projeto e ajude outras pessoas a construir. Documente medidas, materiais e o passo a passo.
              </p>
              <Button
                data-testid="cta-publish-btn"
                size="lg"
                onClick={() => navigate("/publicar")}
                className="mt-7 rounded-full bg-background text-foreground hover:bg-background/90 gap-2 font-semibold"
              >
                <Plus className="h-4 w-4" /> Publicar projeto
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ title, icon: Icon, to, children }) {
  return (
    <section className="py-10">
      <div className="flex items-end justify-between mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <Icon className="h-6 w-6 text-primary" /> {title}
        </h2>
        <Link to={to} className="text-sm font-medium text-primary hover:underline whitespace-nowrap">Ver mais</Link>
      </div>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">{children}</div>;
}
